import { z } from 'zod'

export const REPORT_STATUSES = [
  { value: 'RUNNING', label: 'Running' },
  { value: 'PAUSED', label: 'Paused' },
] as const

export const REPORT_FREQUENCIES = [
  { value: 'DAILY', label: 'Daily' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY', label: 'Monthly' },
] as const

export const REPORT_DATE_RANGES = [
  { value: '7', label: 'Last 7 Days' },
  { value: '15', label: 'Last 15 Days' },
  { value: '30', label: 'Last 30 Days' },
  { value: 'THIS_QUARTER', label: 'This Quarter' },
  { value: 'LAST_QUARTER', label: 'Last Quarter' },
  { value: 'YEAR_TO_DATE', label: 'Year To Date' },
] as const

export const WEEKLY_SEND_DAYS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
] as const

export const MONTHLY_SEND_DAYS = [
  { value: '1', label: '1st Day' },
  { value: '15', label: '15 Day' },
  { value: 'LAST', label: 'Last Day' },
] as const

const requiredText = (label: string) =>
  z.string().trim().min(1, `${label} is required`)

function isHtmlEffectivelyEmpty(value: string): boolean {
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim().length === 0
}

const criteriaScheduleShape = {
  friendly_name: requiredText('Friendly name'),
  report_name: requiredText('Report'),
  date_range: requiredText('Date range'),
  report_status: z.enum(['RUNNING', 'PAUSED'], {
    message: 'Status is required',
  }),
  frequency: z.enum(['DAILY', 'WEEKLY', 'MONTHLY'], {
    message: 'Frequency is required',
  }),
  send_day: z.string().optional(),
  send_time: requiredText('Send time'),
  start_date: requiredText('Start date').refine((value) => value <= '2099-12-30', {
    message: 'Start date must be on or before 12/30/2099',
  }),
  fk_company_id: z.array(z.number()).min(1, 'Select at least one PMC recipient'),
}

function refineSendDayWhenNotDaily(
  values: { frequency: string; send_day?: string | undefined },
  ctx: z.RefinementCtx,
) {
  if (values.frequency !== 'DAILY' && !values.send_day?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['send_day'],
      message: 'Send day is required',
    })
  }
}

/** Criteria & schedule fields only — used to gate the Email Template step. */
export const reportScheduleCriteriaStepSchema = z
  .object(criteriaScheduleShape)
  .superRefine(refineSendDayWhenNotDaily)

export const reportScheduleFormSchema = z
  .object({
    ...criteriaScheduleShape,
    listed: z.enum(['', '1', '0']),
    email_from: requiredText('From'),
    email_to: requiredText('To'),
    email_cc: z.string(),
    email_bcc: z.string(),
    reply_to: z.string(),
    email_subject: requiredText('Subject'),
    email_body: z.string().refine((value) => !isHtmlEffectivelyEmpty(value), {
      message: 'Email body is required',
    }),
  })
  .superRefine(refineSendDayWhenNotDaily)

export type ReportScheduleFormValues = z.infer<typeof reportScheduleFormSchema>
